import axios from 'axios'
import { Preferences } from '@capacitor/preferences'
import { fetchPhaseForDate } from '../../helpers/dataFetcher'

// Helper functions for preferences
async function getPreference(key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

async function setPreference(key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

// state
export const state = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currentDate: new Date().toISOString().split('T')[0],
  isLoading: true,

  periodAnalysis: null,
  streak: null,
  
  // Individual loading states
  loadingPeriodAnalysis: false,
  loadingStreak: false
}

// getters
export const getters = {
  todayCurrentDate: state => state.currentDate,
  isTodayCurrentDate: state => date => date === state.currentDate,
  todayIsLoading: state => state.isLoading,

  periodAnalysis: state => state.periodAnalysis,
  streak: state => state.streak || { current_streak: 0, longest_streak: 0 },
  
  // Individual loading state getters
  isLoadingPeriodAnalysis: state => state.loadingPeriodAnalysis,
  isLoadingStreak: state => state.loadingStreak,
  
  // Combined loading state for backwards compatibility
  hasAnyLoading: state => (
    state.loadingPeriodAnalysis || 
    state.loadingStreak
  )
}

// mutations
export const mutations = {
  SET_DASHBOARD_DATA(state, { key, value }) {
    state[key] = value
  },
  UPDATE_DASHBOARD_DATA(state, data) {
    Object.entries(data).forEach(([key, value]) => {
      state[key] = value
    })
  },
  ADD_ITEM(state, { type, item }) {
    state[type] = [...state[type], item]
  },
  UPDATE_ITEM(state, { type, item }) {
    const index = state[type].findIndex(i => i.id === item.id)
    if (index !== -1) {
      state[type].splice(index, 1, item)
    }
  },
  REMOVE_ITEM(state, { type, id }) {
    state[type] = state[type].filter(item => item.id !== id)
  }
}

// actions
export const actions = {
  async fetchDashboardData({ dispatch, state }, date = null) {
    if (!date) {
      date = state.currentDate
    }

    // Dispatch all individual fetch actions in parallel
    await Promise.all([
      dispatch('fetchPeriodAnalysisData', date),
      dispatch('fetchStreak')
    ])
  },

  async fetchPeriodAnalysisData({ commit, state }, date = null) {
    if (!date) date = state.currentDate
    
    commit('SET_DASHBOARD_DATA', { key: 'loadingPeriodAnalysis', value: true })
    
    try {
      // Clear existing period analysis before fetching fresh data from DB
      commit('UPDATE_DASHBOARD_DATA', { periodAnalysis: null })
      
      const periodAnalysis = await fetchPhaseForDate(date, state.timezone)
      commit('UPDATE_DASHBOARD_DATA', { periodAnalysis })
    } catch (error) {
      console.error('Error fetching period analysis:', error)
    } finally {
      commit('SET_DASHBOARD_DATA', { key: 'loadingPeriodAnalysis', value: false })
    }
  },


  async fetchStreak({ commit }) {
    commit('SET_DASHBOARD_DATA', { key: 'loadingStreak', value: true })
    
    try {
      // Clear existing streak data before fetching fresh data from DB
      commit('SET_DASHBOARD_DATA', { 
        key: 'streak', 
        value: { current_streak: 0, longest_streak: 0 }
      })
      
      const response = await axios.get('/streak')
      commit('SET_DASHBOARD_DATA', { 
        key: 'streak', 
        value: response.data || { current_streak: 0, longest_streak: 0 }
      })
    } catch (error) {
      console.error('Error fetching streak data:', error)
      commit('SET_DASHBOARD_DATA', { 
        key: 'streak', 
        value: { current_streak: 0, longest_streak: 0 }
      })
    } finally {
      commit('SET_DASHBOARD_DATA', { key: 'loadingStreak', value: false })
    }
  }
}





// Helper function to format date-time strings
function formatDateTime(dateTimeString) {
  const date = new Date(dateTimeString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}